import { useCallback, useEffect } from 'react';

/**
 * reCAPTCHA v3. Invisible: no widget, no user interaction — a token is minted on
 * submit and the backend scores it.
 *
 * Inert when VITE_RECAPTCHA_SITE_KEY is unset, so local dev runs without keys and
 * the control switches itself on once the key is in the build environment.
 */

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;
const SCRIPT_ID = 'recaptcha-v3';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// Module scope, not component scope: the script is a page-level resource, and
// remounting the login form must not inject a second copy of it.
let scriptPromise: Promise<void> | null = null;

function loadScript(siteKey: string): Promise<void> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      scriptPromise = null;
      script.remove();
      reject(new Error('Failed to load reCAPTCHA'));
    };

    document.head.appendChild(script);
  });

  return scriptPromise;
}

export default function useRecaptcha() {
  const enabled = Boolean(SITE_KEY);

  // Warm the script on mount so submitting does not wait on a cold network fetch.
  useEffect(() => {
    if (!enabled) return;
    loadScript(SITE_KEY as string).catch(() => undefined);
  }, [enabled]);

  /**
   * Returns the token, or null when it could not be obtained — most often an ad
   * blocker stopping the Google script. The caller must treat null as a hard stop
   * and say so, because the backend rejects a tokenless login and the user would
   * otherwise just see "Invalid credentials" for a correct password.
   */
  const execute = useCallback(
    async (action: string): Promise<string | null> => {
      if (!enabled) return null;

      try {
        await loadScript(SITE_KEY as string);

        return await new Promise<string>((resolve, reject) => {
          if (!window.grecaptcha) {
            reject(new Error('reCAPTCHA unavailable'));
            return;
          }
          window.grecaptcha.ready(() => {
            (window.grecaptcha as NonNullable<Window['grecaptcha']>)
              .execute(SITE_KEY as string, { action })
              .then(resolve, reject);
          });
        });
      } catch {
        return null;
      }
    },
    [enabled],
  );

  return { enabled, execute };
}
